import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Button,
  DotsHorizontal,
  Edit03,
  Menu,
  MenuItem,
  ModalPresetConfirm,
  Popper,
  Trash,
  useModal,
} from '@dashboard/ui'
import {
  PageHeader,
  Table,
  TableCell,
  TableCellBulkCheckbox,
  TableCellBulkToolbar,
  TableRow,
  TableRowSkeleton,
  TableZeroState,
  ToastSeverity,
  useToast,
} from '@/ui'
import { DotsGridSix, Plus } from '@/icons'
import { itemCategoriesQuery, useItemCategoryDeleteMutate } from './api'
import { ItemCategoryRead, StoreCategoryRenderType } from '@/api/dashboard'
import { dashboardClient } from '@/api'
import { useBulkSelect } from '@/libs'
import { EditItemCategoryDialog } from './components/EditItemCategoryDialog'
import { ReactSortable } from 'react-sortablejs'
import { useQuery } from '@tanstack/react-query'
import { useCurrentUser } from '@/api/useCurrentUser'
import { useTranslation } from 'react-i18next'
import { DEFAULT_IMAGE_PLACEHOLDER_ITEM } from '@/Settings'
import { HubSectionIconKey, getHubSectionIcon } from '@/layouts/hub'
import { DEFAULT_LOAD_LIMIT } from '@/api/useGameItems'

export default function ItemCategoryPage() {
  const { companyId, gameId } = useParams() as {
    companyId: string
    gameId: string
  }
  const { t } = useTranslation()
  const [isDirty, setIsDirty] = useState(false)
  const { canEdit } = useCurrentUser()

  const {
    data: items = [],
    isLoading,
    refetch,
  } = useQuery({
    ...itemCategoriesQuery(companyId, gameId, { limit: DEFAULT_LOAD_LIMIT }),
    refetchOnMount: true,
  })
  const { mutateAsync: deleteMutateAsync } = useItemCategoryDeleteMutate(companyId, gameId)
  const { selected, onSelect, onReset } = useBulkSelect()
  const [itemsCategories, setItemsCategories] = useState<ItemCategoryRead[]>([])
  const showToast = useToast()

  useEffect(() => {
    setItemsCategories(items.map(it => ({ ...it })))
  }, [items, isLoading])

  const openBulkDeleteModal = useModal<{ subMessage: string }>(props => (
    <ModalPresetConfirm
      {...props}
      title={{
        children: t('confirm.title'),
        subtitle: props.subMessage,
      }}
      cancel={{
        label: t('Cancel'),
      }}
      submit={{
        callback: async () => {
          await onConfirmDeleteItems()
          props.onClose?.()
        },
        color: 'danger',
        label: t('remove'),
      }}
    />
  ))

  const openDeleteModal = useModal<{ item: ItemCategoryRead }>(props => (
    <ModalPresetConfirm
      {...props}
      title={{
        children: t('confirm.title'),
        subtitle: t('confirm.text', { name: props.item.name }),
      }}
      cancel={{
        label: t('Cancel'),
      }}
      submit={{
        callback: async () => {
          await onDeleteCategoryClick(props.item)
          props.onClose?.()
        },
        color: 'danger',
        label: t('remove'),
      }}
    />
  ))

  const openEditModal = useModal<{ item: ItemCategoryRead }>(props => <EditItemCategoryDialog {...props} />)

  const onClickBulkDelete = useCallback(() => {
    openBulkDeleteModal({ subMessage: t('item-category.remove-n-items', { N: selected.size }) })
  }, [items, selected, openBulkDeleteModal])

  const onDeleteCategoryClick = async (item: ItemCategoryRead) => {
    await deleteMutateAsync({ id: item.id })
  }

  const onConfirmDeleteItems = async () => {
    await dashboardClient.v1.bulkDeleteItemCategories(
      companyId,
      gameId,
      Array.from(selected).map(id => ({ id })),
    )

    onReset([])
    await refetch()
  }

  const onDiscardClick = () => {
    setItemsCategories(items.map(it => ({ ...it })))
    setIsDirty(false)
  }

  const saveOrder = async () => {
    await dashboardClient.v1.bulkUpdateItemCategories(
      companyId,
      gameId,
      itemsCategories.map((it, idx) => ({
        id: it.id,
        position: idx,
      })),
    )
    refetch()
    setIsDirty(false)
    showToast({ message: t('saved'), severity: ToastSeverity.success })
  }

  const renderDefaultHeader = () => {
    return (
      <TableRow variant="header">
        {items.length > 0 && canEdit && (
          <>
            <TableCell width="5%" />
            <TableCellBulkCheckbox
              selected={selected.size > 0 && selected.size == items.length}
              disabled={items.length === 0}
              onChange={() => onReset(selected.size == items.length ? [] : items.map(i => i.id))}
            />
          </>
        )}
        <TableCell width="200%">{t('item-category.name')}</TableCell>
        <TableCell width="200%">{t('item-category.slug')}</TableCell>
        <TableCell width="200%">{t('item-category.description')}</TableCell>
        <TableCell width="200%">{t('item-category.render_type')}</TableCell>
        <TableCell width="30%" />
      </TableRow>
    )
  }

  const renderBulkSelectionHeader = () => (
    <TableRow variant="header">
      <TableCell width={28} />
      <TableCellBulkCheckbox
        selected={selected.size > 0 && selected.size == items.length}
        disabled={items.length === 0}
        onChange={() => onReset(selected.size == items.length ? [] : items.map(i => i.id))}
      />
      <TableCellBulkToolbar selected={selected} onReset={() => onReset([])}>
        <Button variant="tertiary" color="danger" size="sm" onClick={onClickBulkDelete}>
          <Trash size={16} />
          <span>{t('remove')}</span>
        </Button>
      </TableCellBulkToolbar>
    </TableRow>
  )

  const onMainBtnClick = () => {
    if (isDirty) {
      saveOrder()
    } else {
      openEditModal({ item: { name: '', description: '', position: items.length } as ItemCategoryRead })
    }
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        extra={
          canEdit &&
          !!items.length && (
            <>
              {isDirty && (
                <Button variant="outline" color="secondary" size="sm" onClick={onDiscardClick}>
                  {t('Discard')}
                </Button>
              )}
              <Button size="sm" onClick={onMainBtnClick}>
                {t(isDirty ? 'Save2' : 'item-category.create')}
              </Button>
            </>
          )
        }
      >
        {t('sidebar.categories')}
      </PageHeader>

      {isLoading ? (
        <TableRowSkeleton rowCount={10} columnCount={3} />
      ) : items.length === 0 ? (
        <TableZeroState
          title={t(canEdit ? 'item-category.empty-table.title' : 'item-category.empty-table.title.read-only')}
          message={t(canEdit ? 'item-category.empty-table' : 'item-category.empty-table.read-only')}
          buttons={
            canEdit && (
              <Button
                size="sm"
                onClick={() =>
                  openEditModal({
                    item: { name: '', description: '', position: 0 } as ItemCategoryRead,
                  })
                }
              >
                <Plus size={16} />
                <span>{t('item-category.create')}</span>
              </Button>
            )
          }
        />
      ) : (
        <>
          <Table>
            {selected.size == 0 ? renderDefaultHeader() : renderBulkSelectionHeader()}
            <ReactSortable
              animation={200}
              delay={2}
              disabled={!canEdit}
              filter=".non-drag"
              list={itemsCategories || []}
              setList={v => {
                let changed = false
                for (let i = 0; i < v.length; i++) {
                  if (v[i].id != itemsCategories[i].id) {
                    changed = true
                    break
                  }
                }
                setItemsCategories(v)
                if (changed) {
                  setIsDirty(true)
                }
              }}
            >
              {itemsCategories.map(it => (
                <TableRow key={it.id}>
                  {canEdit && (
                    <>
                      <TableCell width="5%">
                        <DotsGridSix className="cursor-move" />
                      </TableCell>
                      <TableCellBulkCheckbox selected={selected.has(it.id)} onChange={() => onSelect(it.id)} />
                    </>
                  )}

                  <TableCell width="200%" className="flex items-center gap-3">
                    {it.icon ? (
                      <div className="flex size-[38px] shrink-0 items-center justify-center rounded-md" style={{}}>
                        <img
                          className="size-5"
                          src={getHubSectionIcon(it.icon as HubSectionIconKey) as string}
                          alt={it.icon}
                        />
                      </div>
                    ) : (
                      <div
                        className="size-[38px] shrink-0 rounded-md bg-cover bg-center bg-no-repeat"
                        style={{
                          backgroundImage: `url(${DEFAULT_IMAGE_PLACEHOLDER_ITEM})`,
                        }}
                      />
                    )}

                    {it.name}
                  </TableCell>
                  <TableCell width="200%">{it.slug}</TableCell>
                  <TableCell width="200%">{it.description}</TableCell>
                  <TableCell width="200%">
                    {t(`item-category.render_type.${it.render_type || StoreCategoryRenderType.Default}`)}
                  </TableCell>
                  <TableCell width="30%">
                    {canEdit && (
                      <Popper
                        popper={props => (
                          <Menu {...props}>
                            <MenuItem
                              icon={Edit03}
                              onClick={() => {
                                openEditModal({ item: it })
                                props.onClose()
                              }}
                            >
                              {t('edit')}
                            </MenuItem>
                            <MenuItem
                              icon={Trash}
                              variant="danger"
                              onClick={() => {
                                openDeleteModal({ item: it })
                                props.onClose()
                              }}
                            >
                              {t('delete')}
                            </MenuItem>
                          </Menu>
                        )}
                      >
                        <Button variant="tertiary" color="secondary">
                          <DotsHorizontal />
                        </Button>
                      </Popper>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </ReactSortable>
          </Table>
        </>
      )}
    </div>
  )
}
